import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  ClipboardCheck, Calendar, Users, CheckCircle, XCircle,
  Clock, AlertCircle, Download, Eye, RefreshCw, Play,
  Square, UserCheck, UserX, PenTool, Save, QrCode, Link2, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

const TrainerAttendancePage = ({ onMenuClick }) => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetDetail, setSheetDetail] = useState(null);
  
  // Signature modal
  const [showSignModal, setShowSignModal] = useState(false);
  const [trainerComment, setTrainerComment] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Create sheet from event
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [trainerEvents, setTrainerEvents] = useState([]);
  const [creating, setCreating] = useState(false);

  const fetchSheets = useCallback(async () => {
    try {
      const response = await api.get('/attendance/trainer/sheets');
      setSheets(response.data);
    } catch (error) {
      console.error('Erreur chargement émargements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const openCreateModal = async () => {
    try {
      const res = await api.get('/planning/trainer/events');
      const courseEvents = (res.data || []).filter(e =>
        ['cours', 'cours_en_ligne', 'cours_physique'].includes(e.event_type)
      );
      setTrainerEvents(courseEvents);
    } catch { setTrainerEvents([]); }
    setShowCreateModal(true);
  };

  const createSheetForEvent = async (eventId) => {
    setCreating(true);
    try {
      await api.post(`/attendance/trainer/generate-for-event/${eventId}`);
      toast.success('Feuille d\'émargement créée');
      setShowCreateModal(false);
      fetchSheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  const openDetailModal = async (sheet) => {
    setSelectedSheet(sheet);
    setShowDetailModal(true);
    try {
      const response = await api.get(`/attendance/sheets/${sheet.id}`);
      setSheetDetail(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const openSheet = async (sheetId) => {
    try {
      await api.post(`/attendance/trainer/sheets/${sheetId}/open`);
      toast.success('Émargement ouvert aux étudiants');
      fetchSheets();
      if (sheetDetail) {
        const response = await api.get(`/attendance/sheets/${sheetId}`);
        setSheetDetail(response.data);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture');
    }
  };

  const closeSheet = async (sheetId) => {
    try {
      await api.post(`/attendance/trainer/sheets/${sheetId}/close`);
      toast.success('Émargement fermé');
      fetchSheets();
      if (sheetDetail) {
        const response = await api.get(`/attendance/sheets/${sheetId}`);
        setSheetDetail(response.data);
      }
    } catch (error) {
      toast.error('Erreur lors de la fermeture');
    }
  };

  const updateStudentStatus = async (studentId, status) => {
    try {
      await api.put(`/attendance/sheets/${selectedSheet.id}/student/${studentId}/status`, { status });
      const response = await api.get(`/attendance/sheets/${selectedSheet.id}`);
      setSheetDetail(response.data);
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Signature canvas handlers
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e1b4b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  useEffect(() => {
    if (showSignModal) {
      setTimeout(initCanvas, 100);
    }
  }, [showSignModal]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    initCanvas();
  };

  const openSignModal = () => {
    setShowSignModal(true);
    setTrainerComment('');
    setSignatureData(null);
  };

  const signSheet = async () => {
    const canvas = canvasRef.current;
    const sigData = canvas?.toDataURL('image/png');
    
    try {
      await api.post(`/attendance/trainer/sheets/${selectedSheet.id}/sign`, {
        signature_data: sigData,
        comment: trainerComment
      });
      toast.success('Feuille validée et signée');
      setShowSignModal(false);
      setShowDetailModal(false);
      fetchSheets();
    } catch (error) {
      toast.error('Erreur lors de la signature');
    }
  };

  const exportPdf = async (sheetId) => {
    try {
      const response = await api.get(`/attendance/sheets/${sheetId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `emargement_${sheetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-100 text-slate-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-amber-100 text-amber-700',
      validated: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Terminé',
      validated: 'Validé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStudentStatusBadge = (status) => {
    const config = {
      present: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Présent', icon: CheckCircle },
      absent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Absent', icon: XCircle },
      excused: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Excusé', icon: AlertCircle },
      late: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Retard', icon: Clock }
    };
    const c = config[status] || config.absent;
    const Icon = c.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  // Today's sheets
  const today = new Date().toISOString().split('T')[0];
  const todaySheets = sheets.filter(s => s.event_date === today);
  const upcomingSheets = sheets.filter(s => s.event_date > today);
  const pastSheets = sheets.filter(s => s.event_date < today);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8 text-violet-600" />
              Émargement
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Gérez les feuilles de présence de vos cours
            </p>
          </div>
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Créer une feuille
          </Button>
        </div>

        {/* Today's Sessions */}
        {todaySheets.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Aujourd'hui
            </h2>
            <div className="grid gap-4">
              {todaySheets.map(sheet => (
                <Card key={sheet.id} className="border-l-4 border-l-violet-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{sheet.event_title}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {sheet.start_time} - {sheet.end_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {sheet.group_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <UserCheck className="w-4 h-4 text-emerald-600" />
                            {sheet.present_count}/{sheet.total_students}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {getStatusBadge(sheet.status)}
                        
                        {sheet.status === 'pending' && (
                          <Button 
                            size="sm" 
                            onClick={() => openSheet(sheet.id)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Ouvrir
                          </Button>
                        )}
                        
                        {sheet.status === 'in_progress' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openDetailModal(sheet)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Gérer
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => closeSheet(sheet.id)}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              <Square className="w-4 h-4 mr-2" />
                              Fermer
                            </Button>
                          </>
                        )}
                        
                        {(sheet.status === 'completed' || sheet.status === 'validated') && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openDetailModal(sheet)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Voir
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming */}
        {upcomingSheets.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4">À venir</h2>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Cours</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Groupe</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingSheets.slice(0, 5).map(sheet => (
                      <tr key={sheet.id} className="border-b">
                        <td className="py-3 px-4">
                          {new Date(sheet.event_date).toLocaleDateString('fr-FR')} {sheet.start_time}
                        </td>
                        <td className="py-3 px-4 font-medium">{sheet.event_title}</td>
                        <td className="py-3 px-4">{sheet.group_name}</td>
                        <td className="py-3 px-4 text-center">{getStatusBadge(sheet.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Past */}
        {pastSheets.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Passées</h2>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Cours</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Groupe</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Présents</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Statut</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastSheets.slice(0, 10).map(sheet => (
                      <tr key={sheet.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                        <td className="py-3 px-4">
                          {new Date(sheet.event_date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="py-3 px-4 font-medium">{sheet.event_title}</td>
                        <td className="py-3 px-4">{sheet.group_name}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-emerald-600 font-medium">{sheet.present_count}</span>
                          /{sheet.total_students}
                        </td>
                        <td className="py-3 px-4 text-center">{getStatusBadge(sheet.status)}</td>
                        <td className="py-3 px-4 text-center">
                          <Button variant="ghost" size="sm" onClick={() => openDetailModal(sheet)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => exportPdf(sheet.id)}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {sheets.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune feuille d'émargement</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-violet-600" />
              {sheetDetail?.event_title}
            </DialogTitle>
            <DialogDescription>
              {sheetDetail?.event_date && new Date(sheetDetail.event_date).toLocaleDateString('fr-FR')} - {sheetDetail?.start_time} à {sheetDetail?.end_time}
            </DialogDescription>
          </DialogHeader>
          
          {sheetDetail && (
            <div className="space-y-6 py-4">
              {/* Status actions */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <span className="text-sm text-slate-500">Statut actuel :</span>
                  <span className="ml-2">{getStatusBadge(sheetDetail.status)}</span>
                </div>
                
                <div className="flex gap-2">
                  {sheetDetail.status === 'pending' && (
                    <Button size="sm" onClick={() => openSheet(sheetDetail.id)} className="bg-blue-600 hover:bg-blue-700">
                      <Play className="w-4 h-4 mr-2" />
                      Ouvrir l'émargement
                    </Button>
                  )}
                  {sheetDetail.status === 'in_progress' && (
                    <Button size="sm" onClick={() => closeSheet(sheetDetail.id)} className="bg-amber-600 hover:bg-amber-700">
                      <Square className="w-4 h-4 mr-2" />
                      Fermer l'émargement
                    </Button>
                  )}
                  {sheetDetail.status === 'completed' && !sheetDetail.trainer_signed && (
                    <Button size="sm" onClick={openSignModal} className="bg-emerald-600 hover:bg-emerald-700">
                      <PenTool className="w-4 h-4 mr-2" />
                      Signer et valider
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Summary */}
              <div className="flex items-center gap-6 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-600">{sheetDetail.present_count} présents</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserX className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-600">{sheetDetail.absent_count} absents</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-600">{sheetDetail.excused_count} excusés</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span className="font-medium text-orange-600">{sheetDetail.late_count} retards</span>
                </div>
              </div>
              
              {/* Signatures */}
              <div>
                <h4 className="font-medium mb-3">Liste des étudiants</h4>
                <div className="space-y-2">
                  {sheetDetail.signatures?.map(sig => (
                    <div 
                      key={sig.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                          <span className="text-violet-600 font-medium">
                            {sig.student_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{sig.student_name}</p>
                          {sig.signed_at && (
                            <p className="text-xs text-slate-500">
                              Signé à {new Date(sig.signed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {getStudentStatusBadge(sig.status)}
                        
                        {(sheetDetail.status === 'in_progress' || sheetDetail.status === 'completed') && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => updateStudentStatus(sig.student_id, 'present')}
                              className={`p-1.5 rounded ${sig.status === 'present' ? 'bg-emerald-100' : 'hover:bg-emerald-50'}`}
                            >
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            </button>
                            <button
                              onClick={() => updateStudentStatus(sig.student_id, 'absent')}
                              className={`p-1.5 rounded ${sig.status === 'absent' ? 'bg-red-100' : 'hover:bg-red-50'}`}
                            >
                              <XCircle className="w-4 h-4 text-red-600" />
                            </button>
                            <button
                              onClick={() => updateStudentStatus(sig.student_id, 'excused')}
                              className={`p-1.5 rounded ${sig.status === 'excused' ? 'bg-amber-100' : 'hover:bg-amber-50'}`}
                            >
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                            </button>
                            <button
                              onClick={() => updateStudentStatus(sig.student_id, 'late')}
                              className={`p-1.5 rounded ${sig.status === 'late' ? 'bg-orange-100' : 'hover:bg-orange-50'}`}
                            >
                              <Clock className="w-4 h-4 text-orange-600" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Trainer validation info */}
              {sheetDetail.trainer_signed && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Validé et signé</span>
                  </div>
                  {sheetDetail.trainer_comment && (
                    <p className="text-sm mt-2">{sheetDetail.trainer_comment}</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => exportPdf(selectedSheet?.id)}>
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-violet-600" />
              Signer et valider
            </DialogTitle>
            <DialogDescription>
              Signez pour valider la feuille d'émargement
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Votre signature</label>
              <div className="border-2 border-dashed rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  className="cursor-crosshair w-full"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <Button variant="link" size="sm" onClick={clearCanvas} className="mt-1">
                Effacer
              </Button>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Commentaire (optionnel)</label>
              <Textarea
                value={trainerComment}
                onChange={(e) => setTrainerComment(e.target.value)}
                placeholder="Ajouter un commentaire sur la séance..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignModal(false)}>
              Annuler
            </Button>
            <Button onClick={signSheet} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Sheet Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-violet-600" />
              Créer une feuille d'émargement
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un cours pour générer la feuille de présence
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-96 overflow-y-auto">
            {trainerEvents.length === 0 ? (
              <p className="text-center text-slate-500 py-6">Aucun cours trouvé dans votre planning</p>
            ) : (
              trainerEvents.map(ev => (
                <div key={ev.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <div>
                    <p className="font-medium text-sm">{ev.title}</p>
                    <p className="text-xs text-slate-500">
                      {ev.event_date && new Date(ev.event_date).toLocaleDateString('fr-FR')} • {ev.start_time?.substring(0,5)} – {ev.end_time?.substring(0,5)}
                      {ev.group_name && ` • ${ev.group_name}`}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" disabled={creating} onClick={() => createSheetForEvent(ev.id)}>
                    {creating ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                    Créer
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerAttendancePage;
